import { AsYouType, CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { MaskPipe } from 'ngx-mask';
import { noop, tap } from 'rxjs';

import { AfterViewInit, Component, forwardRef, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { PHONE_MASK_LIST } from '@shared/constants';
import { ConfigService } from '@shared/services/core/config.service';

@Component({
	selector: 'phone-control',
	template: `
		<div class="grid grid-cols-2 gap-x-1">
			<div class="-mt-0.5">
				<ion-item class="ion-no-padding" lines="none">
					<ion-select label="Country Code" vqControl="select" [value]="codeValue" (ionChange)="handleCodeChange($event)">
						<ion-select-option *ngFor="let code of codes" [value]="code.dial_code">{{ code.name }}</ion-select-option>
					</ion-select>
				</ion-item>
			</div>
			<div class="relative">
				<input
					vqControl
					[formControl]="formControl"
					type="tel"
					[validation]="true"
					[prefix]="codeValue + ' '"
					[mask]="mask"
					(blur)="isVvalid()"
				/>
				<p *ngIf="formControl.errors?.required" class="text-xs text-red-500 font-semibold absolute -bottom-4">Phone Number Required</p>
			</div>
		</div>
	`,

	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => PhoneControlComponent),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			multi: true,
			useExisting: forwardRef(() => PhoneControlComponent),
		},
		MaskPipe,
	],
	styles: [
		`
			ion-select::part(icon) {
				left: 48px !important;
			}
		`,
	],
})
export class PhoneControlComponent implements OnInit, ControlValueAccessor, Validator, AfterViewInit {
	formControl: FormControl = new FormControl<string>('',Validators.required);
	codes = PHONE_MASK_LIST;
	codeValue = '+1';
	countryCode: CountryCode = 'US';
	mask = '000 000 0000';
	selectedCode = {
		name: 'USA/Canada',
		dial_code: '+1',
		code: 'US',
		mask: '+1 000 000 0000',
	};
	onChange: (value: string) => void = noop;
	onTouch: () => void = noop;

	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	constructor(
		private maskPipe: MaskPipe,
		private readonly env: ConfigService
	) {}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	ngOnInit() {
		this.formControl.valueChanges
			.subscribe(value => {
				const phone = this.maskPipe.transform(value, this.mask);
				this.onChange(`${this.codeValue} ${phone}`);
			});	
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	ngAfterViewInit() {
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */

	registerOnTouched(fn: () => void): void {
		this.onTouch = fn;
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */

	setDisabledState(isDisabled: boolean): void {
		isDisabled ? this.formControl.disable() : this.formControl.enable();
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - validate
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	validate(control: AbstractControl): ValidationErrors | null {
		const countryCode = this.getCountryCode(control.value);
		const foundCode: any = this.codes.find(e => e.dial_code === countryCode?.countryCode);
		if (!foundCode) return { invalid: true };
		return null;
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	isVvalid() {
		const value = this.formControl.value;
		const countryCode = this.getCountryCode(value);
		const foundCode: any = this.codes.find(e => e.dial_code === countryCode?.countryCode);
		if (!foundCode) return { invalid: true };
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	writeValue(value: string): void {
		if (!value) {
			this.formControl.setValue('', { emitEvent: true });
			return;
		}
		const countryCode = this.getCountryCode(value);
		const foundCode: any = this.codes.find(e => e.dial_code === countryCode?.countryCode);
		if (!foundCode) return;

		this.formControl.setValue(countryCode.withoutCountryCode, { emitEvent: false });
		this.codeValue = countryCode.countryCode;
		
		if (!foundCode) {
			this.codeValue = '+1';
			this.mask = '000 000 0000';
			return;
		}
		this.codeValue = foundCode.dial_code;
		const mask = foundCode.mask.split(' ');
		mask.shift();
		this.mask = mask.join(' ');
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - handleCodeChange
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	public handleCodeChange($event) {
		const code = $event.detail.value;
		const foundCode = this.codes.find(e => e.dial_code === code);
		if (!foundCode) return;
		this.selectedCode = foundCode;
		var mask = foundCode.mask.split(' ')

		mask.shift();
		this.mask = mask.join(' ');
		this.codeValue = foundCode.dial_code;
		this.countryCode = foundCode.code as CountryCode;
		const phone = this.maskPipe.transform(this.formControl.value, this.mask);
		this.formControl.patchValue(phone);
	}

	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - getCountryCode
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	getCountryCode(input) {
		if (!input) return;
		const defaultCountryCode = input.substr(0, 1) !== '+' ? 'US' : null;
		let formatted = new AsYouType(defaultCountryCode).input(input);
		let countryCode = '';
		let withoutCountryCode = formatted;

		if (defaultCountryCode === 'US') {
			countryCode = '+1';
			formatted = '+1 ' + formatted;
		} else {
			const parts = formatted.split(' ');
			countryCode = parts.length > 1 ? parts.shift() : '';
			withoutCountryCode = parts.join(' ');
		}

		return {
			formatted,
			withoutCountryCode,
			countryCode,
		};
	}
}